/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // Prev/next arrows inside each zoom dialog target this element with --zoom-prev / --zoom-next.
    this.addEventListener('command', (event) => {
      if (event.command !== '--zoom-prev' && event.command !== '--zoom-next') return;
      const dialog = event.source?.closest('dialog');
      if (dialog) this.#step(dialog, event.command === '--zoom-next' ? 1 : -1);
    }, opts);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const dialog = event.target instanceof Element ? event.target.closest('dialog[open]') : null;
      if (!dialog || !this.contains(dialog)) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (event.key === 'ArrowRight') !== rtl;
      event.preventDefault();
      this.#step(dialog, forward ? 1 : -1);
    }, opts);
  }

  // Steps through zoom dialogs in the gallery's visible order (selected variant first), wrapping at the ends.
  #step(currentDialog, delta) {
    const triggers = Array.from(this.querySelectorAll('[data-product-gallery] [data-zoom-preload][commandfor]'));
    const ids = triggers.map((trigger) => trigger.getAttribute('commandfor'));
    const index = ids.indexOf(currentDialog.id);
    if (index === -1 || ids.length < 2) return;

    const nextIndex = (index + delta + ids.length) % ids.length;
    const nextDialog = document.getElementById(ids[nextIndex]);
    if (!(nextDialog instanceof HTMLDialogElement)) return;

    this.#preload(triggers[nextIndex]);
    currentDialog.close();
    nextDialog.showModal();
    const sameControl = nextDialog.querySelector(`[command="${delta > 0 ? '--zoom-next' : '--zoom-prev'}"]`);
    if (sameControl instanceof HTMLElement) sameControl.focus();
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
